import { Texture } from "pixi.js";
import { SymbolFitMode } from "@slot/presentation";
import type { SymbolDefinition, SymbolDefinitionResolver } from "@slot/presentation";
import type { SymbolTextures } from "../assets/GameVisualAssets";
import { parseSymbolId } from "../rules/symbols";
import { CELL, COIN_SIZE, MYSTERY_BOARD_SCALE, MYSTERY_REEL_LAYOUT } from "../scene/layout";

/**
 * A cell of a row not in play (a GROW row while the frame does not show it): the presentation's id, never the wire's.
 * The codec gives it to a hidden row's blank, and the grid draws nothing there.
 */
export const OUT_OF_PLAY = "_";

/**
 * An active line's empty cell in the base game (decision 56): the red badge on the cell's red background. The
 * presentation's id, never the wire's: the codec gives it to a blank in an active line's row, and a change of the lines
 * re-skins the board's blanks (`ActiveLinesPresenter`).
 */
export const BLANK_LIT = "B+";

/**
 * The fillers a rolling cell shows on its way past, blurred along the roll: the presentation's ids, never the wire's (a
 * filler never lands; a cell lands on the server's symbol, sharp). A blurred coin shows no value.
 */
export const BLURRED = { blank: "~B", blankLit: "~B+", coin: "~C", mini: "~MINI", minor: "~MINOR", mystery: "~MYS" } as const;
export type BlurredSymbol = keyof typeof BLURRED;
const BLURRED_BY_ID = new Map<string, BlurredSymbol>(Object.entries(BLURRED).map(([kind, id]) => [id, kind as BlurredSymbol]));

/**
 * The board's ids as the cells draw them: a blank as the empty cell's plaque (it rolls in a cell's strip with the coins),
 * every coin kind at the coins' size (a mystery jackpot's frame as a coin's disc, its window on the cell's centre), a
 * cell out of play as nothing, the rolling cells' fillers blurred. A coin's value is part of its
 * id (`C:250`), so one definition serves every value: the coin view draws the number (`CoinSymbolView`). An id the game
 * does not have resolves to null, which the grid draws empty and logs.
 */
export class SymbolCatalog implements SymbolDefinitionResolver {
  private readonly symbols: SymbolTextures;
  private readonly cache = new Map<string, SymbolDefinition>();

  public constructor(symbols: SymbolTextures) {
    this.symbols = symbols;
  }

  public resolveDefinition(symbolId: string): SymbolDefinition | null {
    const cached = this.cache.get(symbolId);
    if (cached) return cached;
    const definition = this.define(symbolId);
    if (definition) this.cache.set(symbolId, definition);
    return definition;
  }

  private define(symbolId: string): SymbolDefinition | null {
    if (symbolId === OUT_OF_PLAY) return { symbolId, texture: Texture.EMPTY, baseScale: 1, fitMode: SymbolFitMode.None, landSoundKey: null };
    if (symbolId === BLANK_LIT) {
      const texture = this.symbols.blankLit ?? this.symbols.blank;
      return { symbolId, texture, baseScale: CELL.blank.width / Math.max(1, texture.orig.width), fitMode: SymbolFitMode.None, landSoundKey: null };
    }
    const blurred = BLURRED_BY_ID.get(symbolId);
    if (blurred) return this.defineBlurred(symbolId, blurred);
    const symbol = parseSymbolId(symbolId);
    if (!symbol) return null;
    let texture: Texture;
    let size: number;
    switch (symbol.kind) {
      case "blank":
        texture = this.symbols.blank;
        size = CELL.blank.width;
        break;
      case "coin":
        texture = this.symbols.coin;
        size = COIN_SIZE;
        break;
      case "jackpot":
        texture = this.symbols.jackpot[symbol.tier];
        size = COIN_SIZE;
        break;
      case "mystery":
        if (symbol.jackpot) return this.defineMysteryJackpot(symbolId);
        texture = this.symbols.mystery;
        size = COIN_SIZE;
        break;
      case "collect":
        texture = this.symbols.collect;
        size = COIN_SIZE;
        break;
    }
    return { symbolId, texture, baseScale: size / Math.max(1, texture.orig.width), fitMode: SymbolFitMode.None, landSoundKey: null };
  }

  /** The mystery jackpot at the board's scale, moved so its window's centre (not its image's) is the cell's. */
  private defineMysteryJackpot(symbolId: string): SymbolDefinition {
    const texture = this.symbols.mysteryJackpot;
    const { symbol, window } = MYSTERY_REEL_LAYOUT;
    return {
      symbolId,
      texture,
      baseScale: (symbol.width * MYSTERY_BOARD_SCALE) / Math.max(1, texture.orig.width),
      fitMode: SymbolFitMode.None,
      offsetX: (symbol.width / 2 - symbol.frameX - window.x) * MYSTERY_BOARD_SCALE,
      offsetY: (symbol.height / 2 - window.y) * MYSTERY_BOARD_SCALE,
      landSoundKey: null,
    };
  }

  /** A filler drawn as wide as its sharp symbol, its streak above and below (the sharp art when the blurred is missing). */
  private defineBlurred(symbolId: string, kind: BlurredSymbol): SymbolDefinition {
    const sharp: Record<BlurredSymbol, Texture> = {
      blank: this.symbols.blank,
      blankLit: this.symbols.blankLit ?? this.symbols.blank,
      coin: this.symbols.coin,
      mini: this.symbols.jackpot.mini,
      minor: this.symbols.jackpot.minor,
      mystery: this.symbols.mystery,
    };
    const texture = this.symbols.blurred[kind] ?? sharp[kind];
    const size = kind === "blank" || kind === "blankLit" ? CELL.blank.width : COIN_SIZE;
    return { symbolId, texture, baseScale: size / Math.max(1, texture.orig.width), fitMode: SymbolFitMode.None, landSoundKey: null };
  }
}
